import { useState, useRef, useEffect } from 'react';
import { Mic, Send, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BAR_COUNT = 28;
const MIN_BAR = 3;
const MAX_BAR = 30;

/**
 * VoiceRecorder — WhatsApp-style voice input for CloudGM chat widgets.
 *
 * Idle   → compact mic button inside the composer row.
 * Active → full-width recording bar: [✕ cancel] [⏺ 0:03] [live scrolling waveform] [➤ send]
 *
 * Props:
 *   - onTranscript(text)      : receives the transcribed text (default flow via /api/ai/transcribe)
 *   - onAudio(blob, mime)     : when provided, the parent handles the audio itself (e.g. one-shot
 *                               voice→reply endpoint). Should return a Promise.
 *   - onRecordingChange(bool) : tells parent to hide/show the composer
 *   - language                : 'ar' | 'en'
 */
export default function VoiceRecorder({
  onTranscript,
  onAudio,
  onRecordingChange,
  language = 'ar',
  disabled = false,
  variant = 'light',
  testId = 'voice-recorder-btn',
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState(() => new Array(BAR_COUNT).fill(MIN_BAR));

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const cancelFlagRef = useRef(false);
  const fallbackRef = useRef(null);
  const levelsRef = useRef(new Array(BAR_COUNT).fill(MIN_BAR));
  const lastPaintRef = useRef(0);

  const notifyParent = (val) => {
    try { onRecordingChange?.(val); } catch { /* noop */ }
  };

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (fallbackRef.current) clearInterval(fallbackRef.current);
    fallbackRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => () => cleanupStream(), []);

  // Live amplitude meter: RMS of the time-domain signal → scrolling bars (WhatsApp style)
  const startVisualizer = async (stream) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    try {
      const ctx = new Ctx();
      if (ctx.state === 'suspended') await ctx.resume();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      ctx.createMediaStreamSource(stream).connect(analyser);
      audioCtxRef.current = ctx;

      const data = new Uint8Array(analyser.fftSize);
      const tick = (ts) => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);          // 0 (silence) … ~0.5 (loud)
        const loud = Math.min(1, Math.pow(rms * 6, 0.8));   // boost quiet speech
        const h = MIN_BAR + loud * (MAX_BAR - MIN_BAR);
        if (ts - lastPaintRef.current > 45) {               // ~22 fps scroll
          lastPaintRef.current = ts;
          const next = levelsRef.current.slice(1);
          next.push(Math.round(h));
          levelsRef.current = next;
          setLevels(next);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return true;
    } catch {
      return false;
    }
  };

  const startFallbackVisualizer = () => {
    let t = 0;
    const id = setInterval(() => {
      t += 1;
      const next = levelsRef.current.slice(1);
      next.push(MIN_BAR + Math.round(12 * Math.abs(Math.sin(t / 2.1)) + Math.random() * 6));
      levelsRef.current = next;
      setLevels(next);
    }, 70);
    return id;
  };

  const finishWithBlob = async (blob, mime) => {
    setIsTranscribing(true);
    try {
      if (onAudio) {
        await onAudio(blob, mime);
        return;
      }
      const token = localStorage.getItem('token');
      const fd = new FormData();
      const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
      fd.append('file', blob, `voice.${ext}`);
      if (language) fd.append('language', language);
      const res = await axios.post(`${BACKEND_URL}/api/ai/transcribe`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 60000,
      });
      const text = (res.data?.text || '').trim();
      if (!text) {
        toast.info(language === 'ar' ? 'لم أتمكن من فهم الصوت، حاول مجدداً' : "Couldn't understand the audio");
        return;
      }
      onTranscript?.(text);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        toast.info(language === 'ar' ? 'سجّل الدخول لاستخدام الرسائل الصوتية' : 'Please sign in to use voice messages');
      } else if (status === 503) {
        toast.info(msg || (language === 'ar' ? 'الخدمة الصوتية غير متاحة حالياً، اكتب رسالتك نصياً.' : 'Voice is temporarily unavailable, please type.'));
      } else {
        toast.error(msg || (language === 'ar' ? 'فشل تفريغ الصوت' : 'Voice transcription failed'));
      }
    } finally {
      setIsTranscribing(false);
      notifyParent(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(language === 'ar' ? 'المتصفح لا يدعم تسجيل الصوت' : 'Browser does not support voice recording');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];
      cancelFlagRef.current = false;
      levelsRef.current = new Array(BAR_COUNT).fill(MIN_BAR);
      setLevels(levelsRef.current);

      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
      const mimeType = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };

      mr.onstop = async () => {
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        const wasCancelled = cancelFlagRef.current;
        cleanupStream();
        setIsRecording(false);
        if (wasCancelled || chunks.length === 0) { notifyParent(false); return; }
        const mime = (mr.mimeType || mimeType || 'audio/webm').split(';')[0];
        const blob = new Blob(chunks, { type: mime });
        if (blob.size < 1000) {
          toast.info(language === 'ar' ? 'التسجيل قصير جداً، جرب مجدداً' : 'Recording too short, try again');
          notifyParent(false);
          return;
        }
        await finishWithBlob(blob, mime);
      };

      mr.start(250);
      setIsRecording(true);
      setElapsed(0);
      notifyParent(true);

      const t0 = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250);

      const ok = await startVisualizer(stream);
      if (!ok) fallbackRef.current = startFallbackVisualizer();
    } catch {
      toast.error(language === 'ar' ? 'يرجى السماح بالوصول للمايكروفون' : 'Please allow microphone access');
      cleanupStream();
      notifyParent(false);
    }
  };

  const stopAndSend = () => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') return;
    cancelFlagRef.current = false;
    mr.stop();
  };

  const cancelRecording = () => {
    const mr = mediaRecorderRef.current;
    cancelFlagRef.current = true;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    } else {
      cleanupStream();
      setIsRecording(false);
      notifyParent(false);
    }
  };

  // ========= EXPANDED BAR (recording / transcribing) =========
  if (isRecording || isTranscribing) {
    const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    return (
      <div
        className="flex-1 h-12 flex items-center gap-2 px-2 rounded-full bg-gradient-to-r from-[#1E9BD9]/10 via-[#38BDF8]/10 to-[#F5A962]/10 border border-[#1E9BD9]/30"
        data-testid="voice-recording-bar"
      >
        <button
          type="button"
          onClick={cancelRecording}
          disabled={isTranscribing}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-white/70 dark:bg-white/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors disabled:opacity-50"
          title={language === 'ar' ? 'إلغاء' : 'Cancel'}
          data-testid="voice-cancel-btn"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1.5 flex-shrink-0 min-w-[42px]">
          <span className={`w-2 h-2 rounded-full bg-red-500 ${isRecording ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-mono text-red-500 dark:text-red-300 tabular-nums">{fmt(elapsed)}</span>
        </div>

        <div className="flex-1 flex items-center justify-center gap-[2px] h-full overflow-hidden" dir="ltr" data-testid="voice-waveform">
          {isTranscribing ? (
            <div className="flex items-center gap-2 text-[#1E9BD9] text-xs">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{language === 'ar' ? 'جارٍ معالجة الصوت…' : 'Processing audio…'}</span>
            </div>
          ) : (
            levels.map((h, i) => (
              <span
                key={i}
                style={{ height: `${h}px`, opacity: 0.45 + (h - MIN_BAR) / (MAX_BAR - MIN_BAR) * 0.55 }}
                className="inline-block w-[3px] rounded-full bg-gradient-to-t from-[#1E9BD9] to-[#F5A962]"
                data-testid={`voice-bar-${i}`}
              />
            ))
          )}
        </div>

        <button
          type="button"
          onClick={stopAndSend}
          disabled={isTranscribing || elapsed < 1}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] text-white flex items-center justify-center shadow-[0_4px_14px_rgba(30,155,217,0.45)] hover:scale-105 transition-transform disabled:opacity-40 disabled:scale-100"
          title={language === 'ar' ? 'إرسال' : 'Send'}
          data-testid="voice-send-btn"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // ========= IDLE: compact mic button =========
  const baseClass = 'flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40';
  const colorClass = variant === 'light'
    ? 'text-gray-500 hover:text-[#1E9BD9] hover:bg-[#1E9BD9]/10'
    : 'text-theme-secondary hover:text-[#1E9BD9] hover:bg-[#1E9BD9]/10';

  return (
    <button
      type="button"
      onClick={startRecording}
      disabled={disabled}
      className={`${baseClass} ${colorClass}`}
      title={language === 'ar' ? 'تسجيل صوتي' : 'Voice recording'}
      data-testid={testId}
    >
      <Mic className="h-5 w-5" />
    </button>
  );
}
